import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Filter} from "../../api/filter-interface";
import {FilterGroup, ReadWrite} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {BooleanFilter} from "../../basic-entity-back/filters/boolean-filter";
import {
    AfterDateFilter,
    BeforeDateFilter, DateFilters,
    StrictlyAfterDateFilter,
    StrictlyBeforeDateFilter,
    BetweenDateFilter
} from "../../basic-entity-back/filters/date-filter";
import {ExistsFilter} from "../../basic-entity-back/filters/exists-filter";
import {NumericFilter} from "../../basic-entity-back/filters/numeric-filter";
import {
    BetweenRangeFilter,
    GreaterThanOrEqRangeFilter,
    GreaterThanRangeFilter, LowerThanOrEqRangeFilter,
    LowerThanRangeFilter, RangeFilters
} from "../../basic-entity-back/filters/range-filters";
import {
    EndSearchFilter,
    ExactSearchFilter,
    MultipleSearchFilter, PartialSearchFilter,
    SimpleSearchFilter,
    StartSearchFilter, WordStartSearchFilter
} from "../../basic-entity-back/filters/search-filter";
import {InternalPropertyMap} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {EntityDatasource} from "../../api/entity.datasource";
import {FormGroup, UntypedFormBuilder, UntypedFormControl} from "@angular/forms";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {SearchService} from "../../services/search.service";
import {RouteFilterTranslator} from "./route-filter-translator";
import {Subscription} from "rxjs";
import {FilterAndData} from "../../api/filter-list";
import {GeneralSearchFilter} from "../../basic-entity-back/filters/general-search-filter";
import {BasicEntityTableConstants} from "../basic-entity-table/basic-entity-table-constants";
import {TypeStr} from "../../basic-entity-back/property-type/type-str";
import {InterfaceProviderService} from "../../basic-entity-back/services/interface-provider.service";
import {FilterListComponent} from "./filter-list/filter-list.component";
import {filtrosAlAbrir, filtrosConBusqueda, paramsDeFiltros} from "./filtros-botones";
import {
    filtrosAlCambiarPanel, filtrosAplicados, filtrosDeBotonYFijos, filtrosPorDefecto, SeccionFiltro, valoresDesdeFiltros
} from "./panel-filtros/panel-filtros";


interface FilterRepresentation {
    name: string;
    icon?: string;
    description: string;
}


@Component({
    selector: 'be-filtering2',
    templateUrl: './basic-entity-filtering2.component.html',
    styleUrls: ['./basic-entity-filtering2.component.scss']
})
export class BasicEntityFiltering2Component<T> implements OnInit, OnDestroy {
    /** Time that the user has to stay without typing before the search is performed */
    private static readonly TIMEOUT_BEFORE_SEARCHING = 500;
    private static readonly FILTER_REPRESENTATION = new Map<Filter | FilterGroup, FilterRepresentation>([
        [BooleanFilter, {
            name: 'Exacto', icon: 'fa-toggle-on',
            description: 'Busca elementos con un valor en esta propiedad de "verdadero" o "falso".'
        }],
        [AfterDateFilter, {
            name: 'Después', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es posterior o igual a cierta fecha.'
        }],
        [BeforeDateFilter, {
            name: 'Antes', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es previo o igual a cierta fecha.'
        }],
        [StrictlyAfterDateFilter, {
            name: 'Estrictamente después', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es estrictamente posterior a cierta fecha.'
        }],
        [StrictlyBeforeDateFilter, {
            name: 'Estrictamente antes', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es estrictamente previo a cierta fecha.'
        }],
        [BetweenDateFilter, {
            name: 'Entre', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad está entre dos fechas.'
        }],
        [ExistsFilter, {
            name: '¿Nulo?', icon: 'fa-times',
            description: 'Busca elementos cuyo valor en esta propiedad es (o no es) nulo.'
        }],
        [NumericFilter, {
            name: 'Exacto', icon: 'fa-equals',
            description: 'Busca elementos cuyo valor en esta propiedad es exactamente el número indicado.'
        }],
        [GreaterThanRangeFilter, {
            name: 'Mayor', icon: 'fa-greater-than',
            description: 'Busca elementos cuyo valor en esta propiedad es mayor que cierto valor.'
        }],
        [GreaterThanOrEqRangeFilter, {
            name: 'Mayor o igual', icon: 'fa-greater-than-equal',
            description: 'Busca elementos cuyo valor en esta propiedad es mayor o igual que cierto valor.'
        }],
        [LowerThanRangeFilter, {
            name: 'Menor', icon: 'fa-less-than',
            description: 'Busca elementos cuyo valor en esta propiedad es menor que cierto valor.'
        }],
        [LowerThanOrEqRangeFilter, {
            name: 'Menor o igual', icon: 'fa-less-than-equal',
            description: 'Busca elementos cuyo valor en esta propiedad es menor o igual que cierto valor.'
        }],
        [BetweenRangeFilter, {
            name: 'Entre', icon: 'fa-band-aid',
            description: 'Busca elementos cuyo valor en esta propiedad se sitúa entre dos valores.'
        }],
        [MultipleSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad contiene un valor dado.'
        }],
        [SimpleSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad contiene un valor dado.'
        }],
        [StartSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad comienza con un valor dado.'
        }],
        [WordStartSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad tiene alguna palabra que comience por los carácteres dados.'
        }],
        [ExactSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad es exactamente alguna de las palabras dadas (separando por espacios, si cabe).'
        }],
        [EndSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad termina con un valor dado.'
        }],
        [PartialSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad contiene un valor determinado.'
        }],
        [DateFilters, {
            name: 'Filtrar', icon: 'fa-calendar',
            description: 'Busca elementos cuya fecha es anterior, posterior o igual a la fecha dada.'
        }],
        [RangeFilters, {
            name: 'Rango', icon: 'fa-band-aid',
            description: 'Busca elementos cuyo valor es mayor que cierto valor, menor que cierto valor o se sitúa entre dos valores.'
        }]
    ]);


    /** Properties to offer filters for */
    @Input() public properties: InternalPropertyMap[];
    /** Properties which can be filtered (obtained from the input properties) */
    public filtrableProperties: InternalPropertyMap[];
    /** The datasource this component should manage to make the filtering */
    @Input() protected dataSource: EntityDatasource<T>;
    /**
     * Con panel (solo las tablas que lo activan, ver `conPanelFiltros` en be-table), el embudo abre el panel de
     * filtros (casillas, textos y fechas; se aplica al momento) en vez de la lista clásica con «Aplicar». Sin él, todo
     * queda como estaba.
     */
    @Input() public panelFiltros: SeccionFiltro[] | null = null;
    /** Se abre el panel (el embudo): la tabla carga entonces las opciones de las relaciones, no al abrir la página */
    @Output() public panelAbierto = new EventEmitter<void>();
    /** Con panel: los filtros que trae la URL */
    public filtrosUrl: FilterAndData[] = [];
    /** Con panel: los de la URL que son del panel, los que pinta (sin la búsqueda general ni lo que no es suyo) */
    public filtrosPanel: FilterAndData[] = [];
    /** Con panel: cuántos filtros del panel hay puestos, el número que va en el embudo */
    public contadorPanel = 0;
    /** Timeout to reload the filters (while you type this timeout is renovated) */
    private _filtersReloadTimeout = null;
    /** Subscription to the general search in the search bar */
    private _searchSubscription: Subscription = null;
    private _queryParamsSubscription: Subscription = null;
    formGroupFiltering: FormGroup;
    dateOperator: any;
    protected readonly TypeStr = TypeStr;
    @ViewChild(FilterListComponent, {static: true}) filterList: FilterListComponent;
    withFilters: any;

    constructor(private _router: Router,
                private _activatedRoute: ActivatedRoute,
                private _searchService: SearchService,
                private _routeFilterTranslator: RouteFilterTranslator,
                private _interfaceProvider: InterfaceProviderService,
                private _fb: UntypedFormBuilder) {
    }

    ngOnInit(): void {
        this.filtrableProperties = this.properties.filter(prop => prop.filters.length > 0);
        // Con panel la lista clásica va oculta y nadie la lee: llenarla crearía sus be-input para nada
        if (!this.panelFiltros) {
            this.filtrableProperties.forEach(fp => this.filterList.addFilter(fp, fp.filters[0]))
        }
        this._searchService.hideSearch = false;
        this._searchSubscription = this._searchService.search.subscribe(
            newSearch => this._redirectGeneralSearch(newSearch)
        );
        this._queryParamsSubscription = this._activatedRoute
            .queryParams.subscribe(this._manageParamsChange.bind(this));
    }


    private _manageParamsChange(params: Params) {
        const filters = {};
        Object.entries(this._routeFilterTranslator.paramsToFilters(params, this.properties))
            .filter(([k, f]) => f.data != null)
            .forEach(([k, f]) => filters[k] = f);

        if (this.panelFiltros) {
            // Sin nada en la URL, la tabla se abre con los valores por defecto del panel (si alguna sección los tiene).
            // Se sustituye la entrada del historial y no se carga: carga la emisión siguiente, ya con ellos. Tras
            // quitarlo todo, la URL lleva `cleared` y no vuelven
            const iniciales = filtrosAlAbrir(Object.values(filters), !!params['cleared'],
                filtrosPorDefecto(this.panelFiltros, this.properties));
            if (iniciales) {
                this._navegarConPanel(iniciales, true);
                return;
            }
        }

        this.dataSource.filtros = Object.values(filters);
        // El embudo se calcula con lo que trae la URL: al volver a la tabla, o al recargar, el
        // componente es nuevo y no ha aplicado él los filtros
        this.withFilters = this.dataSource.filtros.length > 0;
        if (this.panelFiltros) {
            // Al panel solo le llega lo suyo, y el embudo cuenta solo eso: ni la búsqueda general ni lo que ninguna
            // sección reclama (se conservan aparte en cada navegación del panel, ver `aplicarPanel`)
            this.filtrosUrl = this.dataSource.filtros;
            this.filtrosPanel = filtrosDeBotonYFijos(this.filtrosUrl, [], [], this.panelFiltros).panel;
            this.contadorPanel = filtrosAplicados(this.panelFiltros, valoresDesdeFiltros(this.panelFiltros, this.filtrosPanel)).length;
            this.withFilters = this.contadorPanel > 0;
        }
        this.filterList.filters.forEach(fp => {
            const res = this.dataSource.filtros.find(f2 => f2.property === fp.property);
            if (res !== undefined) {
                fp.control.setValue(res, {emitEvent: false});
            }
        });

        const generalSearch = this.dataSource.filtros.find(f => f.filter === GeneralSearchFilter);
        if (generalSearch) {
            this._searchService.currentSearch = generalSearch.data;
        } else {
            this._searchService.currentSearch = null;
        }
        clearTimeout(this._filtersReloadTimeout);
        this._filtersReloadTimeout = setTimeout(
            () => this.dataSource.load(),
            BasicEntityFiltering2Component.TIMEOUT_BEFORE_SEARCHING);
    }


    /**
     * @inheritDoc
     */
    ngOnDestroy(): void {
        this._searchSubscription.unsubscribe();
        this._queryParamsSubscription.unsubscribe();
        // this._searchService.hideSearch = true;
    }

    /**
     * Performs the navigation to set the filter for the general search.
     * If str is null it does nothing.
     * @private
     */
    private _redirectGeneralSearch(str: string | null) {
        if (str == null) {
            return;
        }
        if (this.panelFiltros) {
            // Con panel, la búsqueda general conserva lo que hubiera en el panel (sin él, la búsqueda lo quitaba todo)
            this._navegarConPanel(filtrosConBusqueda(this.filtrosUrl, str));
            return;
        }

        if (str !== '') {
            this._filterNavigation({'gs': new FilterAndData(GeneralSearchFilter, str)});
        } else {
            this._filterNavigation({});
        }
    }

    private _filterNavigation(list: { [id: string]: FilterAndData }) {
        const filtered = {};
        Object.entries(list)
            .filter(([id, f]) => f && f.data != null)
            .forEach(([id, f]) => filtered[id] = f);

        const params = RouteFilterTranslator.sFiltersToParams(filtered);

        // Preserve pagination
        if (this.dataSource.pagination.pageSize) {
            params[BasicEntityTableConstants.PAGINATION_SIZE] = this.dataSource.pagination.pageSize;
        }

        this.withFilters = Object.values(filtered).length > 0;

        this._router.navigate([], {
            queryParams: params,
            queryParamsHandling: ""
        }).catch(reason =>
            console.error("Navigation rejected", reason)
        );
    }

    clearFilters() {
        this._filterNavigation({});
        this.filterList.setFilters({});
        this.filtrableProperties.forEach(fp => this.filterList.addFilter(fp, fp.filters[0]))
    }

    applyFilters() {
        let filterList: { [id: string]: FilterAndData } = {};
        for (const filter of this.filterList.filters) {
            filterList[filter.id] = filter.control.value;
        }
        this._filterNavigation(filterList);
    }

    /**
     * El panel ha cambiado: va a la URL con lo nuevo del panel y todo lo que no es suyo, que se conserva: la búsqueda
     * general y los filtros que ninguna sección reclama (p. ej. el de cliente de una URL antigua)
     */
    aplicarPanel(delPanel: FilterAndData[]) {
        this._navegarConPanel(filtrosAlCambiarPanel(this.filtrosUrl, [], [], this.panelFiltros, delPanel));
    }

    /** @param reemplazar sustituye la entrada del historial en vez de añadir otra (al ir a los valores por defecto) */
    private _navegarConPanel(filtros: FilterAndData[], reemplazar = false) {
        const params = paramsDeFiltros(filtros);
        if (this.dataSource.pagination.pageSize) {
            params[BasicEntityTableConstants.PAGINATION_SIZE] = this.dataSource.pagination.pageSize;
        }
        // Con valores por defecto en el panel, una URL que el usuario deja sin filtros lleva `cleared` para que no
        // vuelvan; y una vez puesto se conserva en esa visita. Sin valores por defecto (Pedidos) no hace falta
        const hayPorDefecto = filtrosPorDefecto(this.panelFiltros, this.properties).length > 0;
        if (hayPorDefecto && (filtros.length === 0 || this._activatedRoute.snapshot.queryParams['cleared'])) {
            params['cleared'] = true;
        }
        this._router.navigate([], {queryParams: params, queryParamsHandling: "", replaceUrl: reemplazar})
            .catch(reason => console.error("Navigation rejected", reason));
    }
}
